import {isEffectivelyOffline, subscribeConnectivity} from './connectivity';
import {offlineDb} from './db';
import {isOwnedSession} from './offlineSessionManager';
import {
  ALREADY_SYNCING_MESSAGE,
  isFailedItemDue,
  syncOutbox,
} from './syncManager';

const DEBOUNCE_MS = 2_000;
const INTERVAL_MS = 60_000;

export interface SyncSchedulerStatus {
  isSyncing: boolean;
  lastError?: string;
  lastRunAt?: string;
}

let started = false;
let inFlight = false;
let rerunRequested = false;
let debounceMs = DEBOUNCE_MS;
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
let stop: (() => void) | undefined;
let status: SyncSchedulerStatus = {isSyncing: false};

/** Test seam: shortens the coalescing debounce so specs need not wait ~2s. */
export const setSyncSchedulerDebounceForTests = (ms: number): void => {
  debounceMs = ms;
};

const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach(listener => listener());

const setStatus = (patch: Partial<SyncSchedulerStatus>): void => {
  status = {...status, ...patch};
  notify();
};

export const subscribeSyncScheduler = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getSyncSchedulerStatus = (): SyncSchedulerStatus => status;

export const isSyncSchedulerStarted = (): boolean => started;

/** True when the session has items ready to sync now (pending, or transient
 * failures past their backoff). Cheap pre-check so idle ticks do no work. */
export const hasDueItems = async (sessionId: string): Promise<boolean> => {
  const count = await offlineDb.outbox
    .where('sessionId')
    .equals(sessionId)
    .filter(
      item =>
        item.status === 'pending' ||
        (item.status === 'failed' && isFailedItemDue(item))
    )
    .count();
  return count > 0;
};

const drainOnce = async (): Promise<void> => {
  if (isEffectivelyOffline()) return;
  // Each pass starts clean so a stale error doesn't outlive its recovery.
  setStatus({isSyncing: true, lastError: undefined});

  const sessions = (
    await offlineDb.sessions.where('status').equals('active').toArray()
  ).filter(isOwnedSession);

  for (const session of sessions) {
    if (!(await hasDueItems(session.id))) continue;
    try {
      await syncOutbox(session.id);
    } catch (error) {
      const message = (error as Error).message;
      // A concurrent sync (this or another tab) is a benign skip, not a failure.
      if (message === ALREADY_SYNCING_MESSAGE) continue;
      setStatus({lastError: message});
    }
  }

  setStatus({isSyncing: false, lastRunAt: new Date().toISOString()});
};

// Single in-flight drain; a request during a run schedules exactly one rerun.
const runDrain = async (): Promise<void> => {
  if (inFlight) {
    rerunRequested = true;
    return;
  }
  inFlight = true;
  try {
    do {
      rerunRequested = false;
      await drainOnce();
    } while (rerunRequested);
  } finally {
    inFlight = false;
  }
};

/** Debounced, coalescing drain request. No-op while offline. */
export const requestDrain = (): void => {
  if (isEffectivelyOffline()) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = undefined;
    void runDrain();
  }, debounceMs);
};

/**
 * Starts the app-lifetime drain scheduler: an initial drain, then drains on
 * reconnect, tab-visible, and a 60s heartbeat. Idempotent; returns a stopper.
 */
export const startSyncScheduler = (): (() => void) => {
  if (started) return stopSyncScheduler;
  started = true;

  requestDrain();

  const unsubscribeConnectivity = subscribeConnectivity(() => {
    if (!isEffectivelyOffline()) requestDrain();
  });

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') requestDrain();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  const interval = setInterval(() => requestDrain(), INTERVAL_MS);

  stop = () => {
    unsubscribeConnectivity();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    clearInterval(interval);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = undefined;
    started = false;
  };

  return stopSyncScheduler;
};

export const stopSyncScheduler = (): void => {
  stop?.();
  stop = undefined;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = undefined;
  started = false;
  inFlight = false;
  rerunRequested = false;
};
